import React, { useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useKeyboardAwareInput } from '@/hooks/useKeyboardAwareInput';

interface AutoTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onEnter?: () => void;
  minRows?: number;
  maxRows?: number;
  onInputFocus?: () => void; // External focus handler from layout
  onInputBlur?: () => void;  // External blur handler from layout
}

const AutoTextarea = React.forwardRef<HTMLTextAreaElement, AutoTextareaProps>(
  ({ onEnter, minRows = 1, maxRows = 5, className, onKeyDown, onFocus, onBlur, onInputFocus, onInputBlur, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const combinedRef = ref || textareaRef;
    const { handleInputFocus, handleInputBlur } = useKeyboardAwareInput();

    const adjustHeight = () => {
      const textarea = (combinedRef as React.RefObject<HTMLTextAreaElement>)?.current;
      if (!textarea) return;

      textarea.style.height = 'auto';
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10) || 20;
      const minHeight = lineHeight * minRows;
      const maxHeight = lineHeight * maxRows;
      const scrollHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      
      textarea.style.height = `${scrollHeight}px`;
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    };

    useEffect(() => {
      adjustHeight();
    }, [props.value]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && onEnter) {
        e.preventDefault();
        onEnter();
      }
      onKeyDown?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Call internal keyboard handler
      handleInputFocus();
      // Call external handler if provided (from CompanionChatLayout)
      onInputFocus?.();
      // Call original onFocus prop
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Call internal keyboard handler
      handleInputBlur();
      // Call external handler if provided (from CompanionChatLayout)
      onInputBlur?.();
      // Call original onBlur prop
      onBlur?.(e);
    };

    return (
      <textarea
        ref={combinedRef}
        className={cn(
          "w-full resize-none border-input bg-background px-3 py-2 text-base ring-offset-background",
          "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-md border",
          className
        )}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        rows={minRows}
        {...props}
      />
    );
  }
);

AutoTextarea.displayName = 'AutoTextarea';

export default AutoTextarea;